import { Image } from 'shared/components/Image';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { buildSectionLinkUrl } from 'homepage/utils/buildSectionLinkUrl';
import { Caret } from 'shared/icons/Caret';
import { DESKTOP_CATEGORY_CARD_WIDTH } from 'homepage/styles/constants';
import { FeatureFlags } from 'featureFlags/constants';
import { fetchCategoryListSection } from 'homepage/services/homepageApi';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { variableFontRegular } from 'styles/theme/text';

import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IConstructorResult } from 'shared/types/ConstructorResult';
import { IConstructorGroup } from 'shared/types/ConstructorGroup';
import { ISearchConfig } from 'homepage/types/SectionSearchConfig';

export const CategoryCard = ({
  constructorResponse,
  constructorUserParams,
  id,
  uuid,
  subsectionIndex,
  isModuleInViewport,
  title,
  onClick,
  searchConfig,
  handleSubsectionUpdate,
}: {
  constructorResponse?: IConstructorBrowseResponse;
  constructorUserParams?: IConstructorUserParams;
  id?: number;
  uuid?: string;
  subsectionIndex: number;
  isModuleInViewport: boolean;
  title?: string;
  onClick: () => void;
  searchConfig?: ISearchConfig;
  handleSubsectionUpdate: (idx: number, res: IConstructorBrowseResponse) => void;
}) => {
  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);
  const { isLoading } = useStoreSelector((state) => state.homepage);
  const { isMobile } = useWindowSize();

  const [cardItems, setCardItems] = useState(constructorResponse);

  useEffect(() => {
    const getCardItems = async () => {
      const response = await fetchCategoryListSection(
        searchConfig,
        constructorUserParams,
        isConsumerSearchEnabled,
      );
      setCardItems(response);
      handleSubsectionUpdate(subsectionIndex, response);
    };
    if (
      isModuleInViewport &&
      !isLoading &&
      !cardItems &&
      constructorUserParams &&
      (isConsumerSearchEnabled || !constructorResponse)
    ) {
      void getCardItems();
    } else if (typeof constructorResponse !== 'undefined' && !cardItems) {
      setCardItems(constructorResponse);
    }
  }, [
    isModuleInViewport,
    isLoading,
    cardItems,
    constructorResponse,
    isConsumerSearchEnabled,
    constructorUserParams,
  ]);

  if (!cardItems?.response?.results) {
    return null;
  }

  const {
    response: { results, groups },
  }: { response?: { results?: IConstructorResult[]; groups?: Partial<IConstructorGroup>[] } } =
    cardItems;

  const linkUrl: string = buildSectionLinkUrl(searchConfig, groups, isConsumerSearchEnabled);

  return (
    <Link href={linkUrl} passHref prefetch={false} legacyBehavior>
      <CategoryCard.Container key={uuid || id} onClick={onClick}>
        <>
          {!isMobile && (
            <CategoryCard.TopOuterImage
              alt={[
                results?.[0]?.data?.brand,
                results?.[0]?.data?.color,
                results?.[0]?.data?.category,
              ]
                .filter(Boolean)
                .join(' ')}
              className="product-image"
              crop={!isMobile}
              disableLazyLoad
              sizes="(max-width: 480px) 75px, 120px"
              src={results?.[0]?.data?.image_url}
            />
          )}
          <CategoryCard.Placeholder />
          <CategoryCard.ImageSet data-qa="image_container">
            {results?.map(
              (result, index) =>
                index < 3 && (
                  <CategoryCard.Image
                    alt={[result?.data?.brand, result?.data?.color, result?.data?.category]
                      .filter(Boolean)
                      .join(' ')}
                    className="product-image"
                    crop={!isMobile}
                    disableLazyLoad
                    key={result?.data?.id}
                    sizes="(max-width: 480px) 75px, 120px"
                    src={result?.data?.image_url}
                  />
                ),
            )}
          </CategoryCard.ImageSet>
          <CategoryCard.TitleAndArrowContainer>
            <CategoryCard.Title data-qa="category_card_title"> {title} </CategoryCard.Title>
            <CategoryCard.CaretWrapper>
              <Caret color="white" direction="right" size={8} />
            </CategoryCard.CaretWrapper>
          </CategoryCard.TitleAndArrowContainer>
          {!isMobile && (
            <CategoryCard.BottomOuterImage
              alt={[
                results?.[2]?.data?.brand,
                results?.[2]?.data?.color,
                results?.[2]?.data?.category,
              ]
                .filter(Boolean)
                .join(' ')}
              className="product-image"
              src={results?.[2]?.data?.image_url}
              crop={!isMobile}
              disableLazyLoad
              sizes="(max-width: 480px) 75px, 120px"
            />
          )}
        </>
      </CategoryCard.Container>
    </Link>
  );
};

CategoryCard.Container = styled.a`
  align-items: center;
  aspect-ratio: 2/3;
  border: ${({ theme }) => theme.borders.opaqueWhite};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: auto;
  margin-right: 8px;
  max-height: 33.45vw;
  min-height: 33.45vw;
  max-width: 22.3vw;
  min-width: 22.3vw;
  padding: 16px 0px;
  position: relative;
  width: 23.5vw;
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    border: none;
    max-height: 44vw;
    min-height: 44vw;
    max-width: 44vw;
    min-width: 44vw;
    width: 44vw;
    margin: calc((100vw - 88vw - 16px) / 4);
    padding: 24px 0;
  }

  img:first-child,
  img:last-child {
    opacity: 0;
    transition: all 0.1s ease-in;

    @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
      max-width: 44%;
      min-width: 44%;
    }
  }

  [data-qa='image_container'] > img.product-image:first-child,
  img:nth-child(3) {
    opacity: 1;
    transition: all 0.3s ease-out;
  }

  &:hover {
    img.product-image:first-child,
    img.product-image:last-child {
      opacity: 1;
      transition: all 0.3s ease-in;
      max-width: 52%;
      min-width: 52%;

      @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
        max-width: 52%;
        min-width: 52%;
      }
    }

    [data-qa='image_container'] > img.product-image:first-child,
    img.product-image:nth-child(3) {
      opacity: 0;
    }

    [data-qa='image_container'] > img.product-image:nth-child(2) {
      height: 100%;
      min-width: 100%;

      @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
        min-width: 52%;
        margin: 0px 8px;
        transition: all 0.3s ease-in;
      }
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-right: 24px;
    padding: 24px 0px;
    aspect-ratio: 3.28/4;
    max-height: 27.19vw;
    min-height: 27.19vw;

    // calculating the exact margin so the cards are more aligned, same above
    max-width: ${DESKTOP_CATEGORY_CARD_WIDTH};
    min-width: ${DESKTOP_CATEGORY_CARD_WIDTH};
  }
`;

CategoryCard.Placeholder = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: none;
  }
`;

CategoryCard.ImageSet = styled.div`
  display: flex;
  justify-content: center;
  height: 66%;
  overflow: hidden;
  ${hideScrollbars};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 128px;
    height: 128px;
  }
`;

CategoryCard.Image = styled(Image)`
  height: auto;
  min-width: 100%;
  transition: all 0.3s ease-out;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    min-width: 44%;
    margin: 0px 8px;
    width: 44%;
  }
`;

CategoryCard.OuterImage = styled(Image)`
  position: absolute;
  opacity: 0;
  min-width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    max-width: 44%;
    min-width: 44%;
  }
`;

CategoryCard.TopOuterImage = styled(CategoryCard.OuterImage)`
  top: -10%;
`;

CategoryCard.BottomOuterImage = styled(CategoryCard.OuterImage)`
  bottom: -10%;
`;

CategoryCard.Title = styled.h3`
  font-weight: 600;
  margin: 0;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  z-index: ${({ theme }) => theme.zIndex.homepageTab};
`;

CategoryCard.CaretWrapper = styled.div`
  margin-left: 4px;
  padding-top: 2px;
  z-index: ${({ theme }) => theme.zIndex.homepageContent};
  display: flex;
  align-items: center;
  height: 100%;
`;

CategoryCard.TitleAndArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    text-decoration: underline;
  }
`;
